/**
 * What the creative section says — a screen that lists tweets, attaches one, and
 * removes one.
 *
 * 🔴 The hardest sentences here are the ones about cost. A promoted tweet is created
 * ACTIVE and X allows no PUT, so attaching is irreversible except by deleting, and the
 * PAUSED-parent rule that makes it safe is **this console's** and not X's. The copy
 * states that, names the window the check cannot close, and argues for neither side:
 * copy that leans produces consent rather than a decision, and this is the screen where
 * an operator puts a creative in front of people.
 *
 * Nothing here is a reading of a live answer. The facts come from X's reference, read
 * 2026-10-07 (`audits/2026-10-07/x-reference-promoted-tweets.md`); the decisions are the
 * owner's, 2026-10-08.
 */
import type { Tweet } from "./api.ts";

/** The three sets X's `tweet_type` names. Required: X offers no "all". */
export const TYPES = ["PUBLISHED", "DRAFT", "SCHEDULED"] as const;

/**
 * The three timelines X's `timeline_type` names.
 *
 * 🔴 `NULLCAST` is X's own default and it means **promoted-only** tweets. The screen
 * therefore makes the operator choose rather than sending nothing: a list that
 * silently excludes everything the account posted organically, shown to someone
 * asking "which of our tweets can we promote", is a wrong answer that looks complete.
 */
export const TIMELINES = ["ORGANIC", "NULLCAST", "ALL"] as const;

/** Beside the timeline switch, so the choice is informed rather than guessed. */
export const TIMELINE_MEANS = {
	ORGANIC: "Tweets posted to the timeline in the usual way.",
	NULLCAST:
		"Promoted-only tweets: created for advertising and not shown to followers. X's own default.",
	ALL: "Both.",
};

/**
 * 🔴 What attaching costs, now that both decisions are taken.
 *
 * The owner decided on 2026-10-08: a promoted tweet may be created, **only under a
 * PAUSED line item**, and this console may delete one. So this copy no longer names
 * open questions — it states what was decided, what it costs, and which part of the
 * safety is ours rather than X's. It still argues for neither side: the operator is
 * about to put a creative in front of people, and the facts have to carry themselves.
 *
 * Nothing here is a reading of a live answer. The facts are X's reference, read
 * 2026-10-07 (`audits/2026-10-07/x-reference-promoted-tweets.md`).
 */
/**
 * Beside the Promote choice (3d, 2026-10-30): a tweet already attached to this line item is not
 * offered again. The worker refuses the repeat with 400 too — this is the mirror, not the rule —
 * and nothing here says what X does with a repeat: the reference does not say.
 */
export const ALREADY_ATTACHED = {
	suffix: "already attached",
	note: "A tweet already attached to this line item is not offered again — this console's rule, not X's: the reference does not say what X does with a repeated attach. Remove it first to attach it again.",
};

export const ATTACHING = {
	title: "Attaching a tweet creates a promoted tweet, and X creates it ACTIVE.",
	body:
		"X's reference shows the object arriving entity_status ACTIVE, with the note that it is not " +
		"possible to update (PUT) a promoted tweet. So it cannot be created switched off and cannot be " +
		"switched off afterwards: the only way to take one back is to delete it, which Remove below does.",
	decided: [
		"A promoted tweet is created only while this line item is PAUSED. That refusal is this console's, decided 2026-10-08 — X asks nothing about the line item's status and would accept the attach either way. The object is born ACTIVE, so the only thing that keeps a new creative from being served is a parent that is not delivering: the safety is a property of the pair rather than of the object.",
		"Remove deletes the promoted tweet in X. It is this console's first delete of anything, decided 2026-10-08, and it exists because X offers no way to switch one off.",
	],
	/**
	 * 🔴 The window, named rather than papered over — e1's requirement for this text,
	 * and the honest half of the PAUSED rule.
	 */
	window:
		"The line item's status is read immediately before the attach, which narrows the window and does not close it: X sells no conditional create, so a line item switched on between that read and the attach would receive a creative that is served at once. After a successful attach this screen re-reads the line item, so what you see is its state now rather than the state the check saw.",
	/**
	 * 🔴 **Rewritten 2026-10-08 by the PR that added the Start button.** It read:
	 * *"Starting delivery stays the owner's act, in X, on the account itself."* The first
	 * half is unchanged — the act is the owner's — and the second stopped being true on
	 * the same day: the owner lifted the refusal of `ACTIVE`, so the switch is on the
	 * line item's own screen in this console. Left as it was, this line would have sent
	 * an operator to `ads.x.com` for something one screen away, and would have made the
	 * window named above read smaller than it is.
	 */
	whose:
		"Starting delivery is the owner's act, and since 2026-10-08 it can be done from this console: Start sits beside Pause on the line item's own screen, not only in ads.x.com. That is why the PAUSED parent above is a safety of the pair rather than of the creative — what you attach is born ACTIVE, and its parent is one screen from being switched on.",
};

/**
 * 🔴 The order of the three acts, said once at the top of the panel. Each act is
 * on a different control — Pause and Start in the line item panel's header, Promote here —
 * and on 2026-10-20 the owner, with an ACTIVE line item, found Promote disabled
 * and nothing that said what came first. Until PR 3 of the one-screen layout (2026-10-29)
 * the first act read «Stop the line item (under Targeting on its row)»: the place moved,
 * and the verb is the button's (B2).
 *
 * 🔴 The tail says only what Start is NECESSARY for, never that it delivers. The
 * first head of #164 said «starting is what makes X serve it» — a promise, the
 * mirror image of the reassurance G0 guards against — while START_IS_NOT_DELIVERY
 * (targeting-copy.ts) says ACTIVE is not a delivery signal and the campaign and X's
 * review decide; on 2026-10-20 both creatives were REJECTED / APPEAL_REQUESTED and
 * the instrument had no payment method, so a Start would have served nothing
 * (the reviewer's 🔴 on #164).
 */
export const ORDER =
	"The order here: Pause the line item (Pause, at the top of this screen), Promote the tweet below, then Start the line item again — X serves nothing while it is PAUSED; whether it serves afterwards is the campaign's and X's review's answer (the Served column, and X's review below), not a promise of this screen.";

/** Under Promote while the line item is not PAUSED: why the button is off. */
export function deliveringNote(entityStatus: string): string {
	return `This line item is ${entityStatus}. Pause it first: a promoted tweet is created ACTIVE and X allows no way to switch one off.`;
}

/** Beside Remove: deleting is the only way back, and what the list does afterwards. */
export const REMOVING = {
	confirm: (id: string) =>
		`Delete promoted tweet ${id} in X? X allows no way to switch one off, so deleting is the only way to stop it being served. This cannot be undone — a new attach makes a different promoted tweet, with a different id.`,
	/**
	 * 🔴 After a delete the row is gone from the list, because deleted rows are not
	 * asked for. Said out loud: an absent row otherwise reads as a row that never
	 * existed, which is the one reading that would make an operator doubt the deletion
	 * happened.
	 */
	gone: (id: string) =>
		`Removed ${id}. X no longer lists it, which is what a deleted promoted tweet looks like here — the list asks only for the live ones.`,
	unknown: (id: string) =>
		`X did not answer the delete of ${id}. Asking again is safe: deleting twice cannot create anything, and the end state is the same. Read the list afterwards to see where it stands.`,
};

/**
 * What X says about the creative itself, which is not the same as whether it exists.
 *
 * 🔴 `approval_status` is X's review. A promoted tweet can exist, be ACTIVE, and still
 * not be shown — so the screen prints the value X sent, and says plainly when X sent
 * none, rather than letting ACTIVE read as "being served".
 *
 * 🔴 For anything but ACCEPTED the sentence also says where the reason is NOT: X's
 * promoted-tweet object carries `approval_status` and no reason field, and the
 * console reads nothing else about the review. On 2026-10-20 the account held
 * `b6419l` REJECTED and `b640mq` APPEAL_REQUESTED (audits/2026-10-20/SUMMARY.md),
 * and a column that names the verdict with no word about the reason sends an
 * operator looking for it on this screen. The reason, and the appeal, are in X Ads
 * Manager; the sentence says so and promises nothing about either.
 */
export function approvalNote(approvalStatus: string | null): string {
	if (approvalStatus === null) return "X sent no approval status for this creative.";
	if (approvalStatus === "ACCEPTED") return "X accepted this creative.";
	return `X's approval status for this creative: ${approvalStatus}. ACTIVE is not the same as being served. X sends no reason through the API — the reason, and an appeal, are in X Ads Manager (ads.x.com), not here.`;
}

/**
 * 🔴 The read by id, added 2026-10-12.
 *
 * Why it exists: the list form of `GET /api/v1/tweets` answered nothing for the one
 * tweet this account has, in every timeline, while X returned the same tweet by
 * `tweet_ids` (audits/2026-10-12/x6-tweet-visibility.txt; docs/START-HERE.md leads
 * that state). So the screen offers a second way to READ — not a second way to name
 * what gets promoted. The typed digits are a question to X; what is listed is X's
 * answer, carrying X's own `id_str`; and the attach still sends only a listed row.
 * A typed id that is wrong by a digit either names nothing (X answers no row) or
 * names another tweet — which is then on screen, with its text, before anyone can
 * promote it. That is the whole difference from a field that fed the attach directly.
 */
export const LOOKUP = {
	label: "Find by id",
	helper:
		"Digits only — a tweet id as X writes it, under the type chosen above. The id is sent to X as a question; what is listed is X's answer, and only a listed row can be promoted.",
	shown:
		"Read by id: the rows above are what X returned for the id asked, not a page of a timeline. A tweet here that is not the one you meant is X answering a different id — read the text before promoting.",
	empty:
		"X returned no tweet for that id under this type. An id that is off by a digit, or a tweet of another type (DRAFT, SCHEDULED), reads as nothing here.",
};

/** A promoted tweet's row, with nothing invented. */
export function promotedLabel(p: { tweetId: string | null; entityStatus: string }): string {
	return p.tweetId === null
		? `tweet id unavailable · ${p.entityStatus}`
		: `${p.tweetId} · ${p.entityStatus}`;
}

/** Under the list: what is shown, and X's own limit on knowing more. */
export function loadedLabel(shown: number, more: boolean): string {
	const rows = `${shown} ${shown === 1 ? "tweet" : "tweets"} shown`;
	return more ? `${rows}. X has more.` : `${rows}.`;
}

/** Beside `loadedLabel` when X has more: «shown», as there — «page» is the table's own (`NEXT_PAGE`). */
export const MORE_THAN_SHOWN = "X has more than those shown";

/**
 * 🔴 Shown when X sent rows this console cannot use.
 *
 * Never hidden: the worker drops a row whose id came only as a number, because a
 * tweet id does not survive JSON as one — and an operator who counts the list
 * against what they see in X deserves to know the difference is ours, not X's.
 */
export function droppedNote(dropped: number): string | null {
	if (dropped <= 0) return null;
	return `${dropped} ${dropped === 1 ? "tweet" : "tweets"} X sent could not be listed: the id arrived only as a number, and a tweet id does not survive JSON as a number — it would name a different tweet. Nothing was guessed.`;
}

/**
 * The word for X's `nullcast`: promoted-only, organic, or nothing when X did not say.
 * One place for the two words — `tweetLabel` below and the campaign card's aside both
 * read it, so neither spells them again.
 */
export function tweetKind(nullcast: boolean | null): string {
	return nullcast === true ? "promoted-only" : nullcast === false ? "organic" : "";
}

/** A tweet as X describes it, with nothing invented and nothing shortened. */
export function tweetLabel(t: Tweet): string {
	const what = t.name ?? t.text ?? "(no text)";
	const kind = tweetKind(t.nullcast);
	return kind ? `${what} · ${kind}` : what;
}

export const WRITES = "Attaching and removing on this screen write to X, on this account.";
